import React, { useState } from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { useLocale } from './LocaleProvider';
import { departmentGroups } from '../data/departmentGroups';
import { company } from '../data/company';
import { Locale } from '../types/content';
type SiteLayoutProps = {
  children: React.ReactNode;
};
const labels: Record<Locale, Record<string, string>> = {
  nl: {
    heritage: `In dienst van onze gemeenschap sinds ${company.foundedYear} — ${company.yearsOfService} jaar`,
    home: 'Home',
    departments: 'Afdelingen',
    brands: 'Merken',
    business: 'Zakelijk',
    gallery: 'Galerij',
    news: 'Nieuws',
    about: 'Over ons',
    contact: 'Contact',
    browse: 'Bekijk alle afdelingen',
    request: 'Informatie aanvragen',
    toggleNav: 'Navigatie openen',
    footerDescription: 'Uitbater van de Carrefour-hypermarkt in Brugge en groothandel voor huishoudens, families en professionals.',
    explore: 'Ontdekken',
    allDepartments: 'Alle afdelingen',
    ourBrands: 'Onze merken',
    storeGallery: 'Winkelgalerij',
    newsPromotions: 'Nieuws & acties',
    businessTitle: 'Zakelijk',
    wholesale: 'Groothandel & distributie',
    corporateSupply: 'Levering aan bedrijven',
    visit: 'Adres',
    contactCompany: 'Neem contact op',
    legal: 'Onderneming',
    index: 'Index'
  },
  en: {
    heritage: `Serving our community since ${company.foundedYear} — ${company.yearsOfService} years`,
    home: 'Home',
    departments: 'Departments',
    brands: 'Brands',
    business: 'Business',
    gallery: 'Gallery',
    news: 'News',
    about: 'About us',
    contact: 'Contact',
    browse: 'Browse all departments',
    request: 'Request information',
    toggleNav: 'Toggle navigation',
    footerDescription: 'Operator of the Carrefour hypermarket in Bruges and wholesaler for homes, families and professionals.',
    explore: 'Explore',
    allDepartments: 'All departments',
    ourBrands: 'Our brands',
    storeGallery: 'In-store gallery',
    newsPromotions: 'News & promotions',
    businessTitle: 'For business',
    wholesale: 'Wholesale & distribution',
    corporateSupply: 'Corporate supply',
    visit: 'Address',
    contactCompany: 'Get in touch',
    legal: 'Company',
    index: 'Index'
  }
};
function LanguageToggle() {
  const {
    locale,
    setLocale
  } = useLocale();
  return <div className="flex items-center border border-ink bg-chalk" aria-label={locale === 'nl' ? 'Kies een taal' : 'Choose language'} data-id="element-20" data-mp="1zmv16ziyzl0r">
      {(['nl', 'en'] as Locale[]).map((option, index) => <button key={option} type="button" onClick={() => setLocale(option)} aria-pressed={locale === option} className={`px-3 py-2 font-mono text-[12px] font-semibold uppercase tracking-[0.14em] transition ${index > 0 ? 'border-l border-ink' : ''} ${locale === option ? 'bg-cobalt text-chalk' : 'bg-chalk text-ink hover:bg-ink hover:text-chalk'}`} data-id="element-21" data-mp="1pq01idaynekw" data-mpi={index}>
          {option}
        </button>)}
    </div>;
}
export function SiteLayout({
  children
}: SiteLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    locale
  } = useLocale();
  const text = labels[locale];
  const navItems: [string, string][] = [[text.home, '/'], [text.departments, '/categories'], [text.brands, '/brands'], [text.business, '/wholesale'], [text.gallery, '/gallery'], [text.news, '/news'], [text.about, '/about'], [text.contact, '/contact']];
  return <div className="min-h-screen w-full bg-paper text-ink" data-id="element-22" data-mp="263wm6jazfj1m">
      <div className="border-b border-ink bg-ink px-5 py-2.5 text-center font-mono text-[10px] uppercase tracking-label text-paper/75" data-id="element-23" data-mp="261ad2f8g0vha">
        {text.heritage}
      </div>

      <header className="border-b border-ink bg-paper" data-id="element-24" data-mp="bpvmxxbisu10">
        <div className="mx-auto flex max-w-[1500px] items-center gap-8 px-5 py-5 lg:px-10" data-id="element-25" data-mp="6r2b09bvtb2y">
          <Link to="/" className="shrink-0" aria-label={company.name} data-id="element-26" data-mp="2tzhqdimdq60g">
            <span className="block font-display text-2xl leading-none tracking-[-0.02em] text-ink sm:text-[28px]" data-id="element-27" data-mp="2u1cboy1kc052">
              Brugge Retail
              <sup className="ml-1 font-mono text-[9px] uppercase tracking-label text-cobalt" data-id="element-28" data-mp="33y7azj5nu4ke">
                nv
              </sup>
            </span>
            <span className="mt-1.5 block font-mono text-[10px] uppercase tracking-label text-ink/50" data-id="element-29" data-mp="37wetaifrvdxp">
              {company.storeName} · {company.city}
            </span>
          </Link>

          <nav className="ml-auto hidden items-center gap-7 font-mono text-[11px] uppercase tracking-[0.16em] lg:flex" aria-label={locale === 'nl' ? 'Hoofdnavigatie' : 'Main navigation'} data-id="element-30" data-mp="2g5h2jjwyu3ev">
            {navItems.slice(0, 7).map(([label, path]) => <NavLink key={path} to={path} end={path === '/'} className={({
            isActive
          }) => isActive ? 'text-cobalt underline decoration-1 underline-offset-[6px]' : 'text-ink/70 hover:text-ink hover:underline hover:decoration-1 hover:underline-offset-[6px]'} data-id="element-31" data-mp="1shqqvcj5uzr7">
                {label}
              </NavLink>)}
            <Link to="/contact" className="border border-ink bg-ink px-4 py-2.5 text-paper transition hover:bg-cobalt hover:border-cobalt" data-id="element-32" data-mp="1az8td72h0gnp">
              {text.request}
            </Link>
            <LanguageToggle data-id="element-33" data-mp="11960r7z9h4pw" />
          </nav>

          <div className="ml-auto flex items-center gap-4 lg:hidden" data-id="element-34" data-mp="waw5yyhg864t">
            <LanguageToggle data-id="element-35" data-mp="w91fl4nerltc" />
            <button onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 border border-ink px-3 py-2 font-mono text-[11px] uppercase tracking-[0.16em]" aria-label={text.toggleNav} aria-expanded={menuOpen} data-id="element-36" data-mp="sac63n5hqoez">
              {menuOpen ? <MinusIcon size={14} data-id="element-37" data-mp="8nr2zn6geltk" /> : <PlusIcon size={14} data-id="element-38" data-mp="8mfr102uu7rd" />}
              {text.index}
            </button>
          </div>
        </div>

        {menuOpen && <nav className="border-t border-ink bg-chalk px-5 py-2 lg:hidden" aria-label={locale === 'nl' ? 'Mobiele navigatie' : 'Mobile navigation'} data-id="element-39" data-mp="3uv8lvdl8f7ee">
            {navItems.map(([label, path]) => <Link key={path} onClick={() => setMenuOpen(false)} to={path} className="flex items-baseline justify-between border-b border-stone py-3.5 font-display text-xl last:border-0" data-id="element-40" data-mp="re8md15ec529">
                {label}
                <span className="font-mono text-[10px] uppercase tracking-label text-ink/40" data-id="element-41" data-mp="mn0a7xcom5le">
                  {path === '/' ? 'start' : path.replace('/', '')}
                </span>
              </Link>)}
          </nav>}
      </header>

      {children}

      <footer className="border-t border-ink bg-ink text-paper" data-id="element-42" data-mp="2zuhyj7s4zjhb">
        <div className="mx-auto max-w-[1500px] px-5 lg:px-10" data-id="element-43" data-mp="2zsnh43g2q9hz">
          <div className="grid gap-10 border-b border-paper/15 py-14 md:grid-cols-2 lg:grid-cols-4" data-id="element-44" data-mp="2uudj7l2ysuso">
            <div className="lg:col-span-1" data-id="element-45" data-mp="39j9nd71whno0">
              <p className="font-display text-3xl leading-tight" data-id="element-46" data-mp="39me391oqa7ui">
                Brugge Retail
                <span className="font-mono text-xs uppercase tracking-label text-cobalt" data-id="element-47" data-mp="34nb4fyzccxuz">
                  {' '}
                  nv
                </span>
              </p>
              <p className="mt-4 max-w-xs text-sm leading-6 text-paper/60" data-id="element-48" data-mp="2by3w68a34twj">
                {text.footerDescription}
              </p>
            </div>
            <FooterLinks title={text.explore} links={[[text.allDepartments, '/categories'], [text.ourBrands, '/brands'], [text.storeGallery, '/gallery'], [text.newsPromotions, '/news']]} data-id="element-49" data-mp="2luyt4mqpmm8p" />
            <FooterLinks title={text.businessTitle} links={[[text.wholesale, '/wholesale'], [text.request, '/contact'], [text.corporateSupply, '/wholesale']]} data-id="element-50" data-mp="1unqiqadfyurf" />
            <div data-id="element-51" data-mp="24jjs5kvv684z">
              <h2 className="font-mono text-[10px] uppercase tracking-label text-paper/45" data-id="element-52" data-mp="29buusvhzo818">
                {text.visit}
              </h2>
              <p className="mt-5 text-sm leading-7 text-paper/75" data-id="element-53" data-mp="2dcwvzhvguu5u">
                {company.street}
                <br data-id="element-54" data-mp="2ibhu9br5r3rn" />
                {company.city}
                <br data-id="element-55" data-mp="2nc5kvwbh5ilc" />
                {company.country}
              </p>
              <a href={`tel:${company.phoneHref}`} className="mt-5 block w-fit border-b border-cobalt pb-0.5 font-mono text-[11px] tracking-[0.12em] text-paper hover:text-cobalt" data-id="element-56" data-mp="2n9t4vopa1iax">
                {company.phone}
              </a>
              <a href={`mailto:${company.email}`} className="mt-3 block w-fit border-b border-cobalt pb-0.5 font-mono text-[11px] tracking-[0.12em] text-paper hover:text-cobalt" data-id="element-57" data-mp="xxhkktg5rr3o">
                {company.email}
              </a>
            </div>
          </div>
          <div className="flex flex-col gap-2 py-6 font-mono text-[10px] uppercase tracking-label text-paper/45 sm:flex-row sm:justify-between" data-id="element-58" data-mp="32f5o8uj74jjm">
            <p data-id="element-59" data-mp="3u5tqk9wlvuyv">
              {text.legal} · {company.enterpriseNumber} · {company.vatNumber}
            </p>
            <p data-id="element-60" data-mp="3q48eurvt5mq9">
              © {new Date().getFullYear()} {company.name}
            </p>
          </div>
        </div>
      </footer>
    </div>;
}
function FooterLinks({
  title,
  links
}: {
  title: string;
  links: [string, string][];
}) {
  return <div data-id="element-61" data-mp="3g8es6fodkcur">
      <h2 className="font-mono text-[10px] uppercase tracking-label text-paper/45" data-id="element-62" data-mp="3g8oi639f6k0g">
        {title}
      </h2>
      <ul className="mt-5 space-y-3 text-sm" data-id="element-63" data-mp="24r5ek9rxgf1d">
        {links.map(([label, path]) => <li key={label} data-id="element-64" data-mp="24thz8up2l3i4">
            <Link className="text-paper/75 underline-offset-4 hover:text-paper hover:underline" to={path} data-id="element-65" data-mp="176gph5c7pvnt">
              {label}
            </Link>
          </li>)}
      </ul>
    </div>;
}